import {
  Timestamp,
  arrayUnion,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import {
  teamUpdateCallRoomId,
  type ITeamUpdateCall,
  type TeamUpdateCallStatus,
} from '../types/TeamUpdate';
import {
  isTeamUpdateCallLive,
  nextTeamUpdateCallAfterLeave,
} from '../utilities/teamUpdateCall';

const CALLS = 'teamUpdateCalls';

function mapCall(id: string, data: Record<string, unknown>): ITeamUpdateCall {
  return {
    id,
    groupId: String(data.groupId || id),
    callRoomId: String(data.callRoomId || teamUpdateCallRoomId(id)),
    status: (data.status as TeamUpdateCallStatus) || 'ended',
    startedBy: String(data.startedBy || ''),
    startedByName: String(data.startedByName || ''),
    participantIds: Array.isArray(data.participantIds)
      ? data.participantIds.map(String)
      : [],
    startedAt: (data.startedAt as Timestamp) || Timestamp.now(),
    updatedAt: (data.updatedAt as Timestamp) || Timestamp.now(),
    endedAt: data.endedAt as Timestamp | undefined,
  };
}

/**
 * Presence for Team Updates group calls. Media uses mesh WebRTC + `signaling`;
 * this collection only tracks who started / who is in the room for join UI.
 */
export class TeamUpdateCallManager {
  static observeCall(
    groupId: string,
    callback: (call: ITeamUpdateCall | null) => void
  ): Unsubscribe {
    return onSnapshot(
      doc(db, CALLS, groupId),
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        callback(mapCall(snap.id, snap.data() as Record<string, unknown>));
      },
      (error) => {
        console.error('Failed to observe team update call', { groupId, error });
        callback(null);
      }
    );
  }

  static async getCall(groupId: string): Promise<ITeamUpdateCall | null> {
    const snap = await getDoc(doc(db, CALLS, groupId));
    if (!snap.exists()) return null;
    return mapCall(snap.id, snap.data() as Record<string, unknown>);
  }

  /** Start or rejoin: marks call live and adds the current user to participants. */
  static async joinCall(input: {
    groupId: string;
    userId: string;
    userName: string;
  }): Promise<ITeamUpdateCall> {
    const { groupId, userId, userName } = input;
    const ref = doc(db, CALLS, groupId);
    const existing = await getDoc(ref);
    const now = Timestamp.now();
    const callRoomId = teamUpdateCallRoomId(groupId);
    const existingCall = existing.exists()
      ? mapCall(groupId, existing.data() as Record<string, unknown>)
      : null;

    if (!isTeamUpdateCallLive(existingCall)) {
      const payload = {
        groupId,
        callRoomId,
        status: 'live' as const,
        startedBy: userId,
        startedByName: userName,
        participantIds: [userId],
        startedAt: now,
        updatedAt: now,
      };
      await setDoc(ref, payload);
      return mapCall(groupId, payload);
    }

    await updateDoc(ref, {
      participantIds: arrayUnion(userId),
      updatedAt: now,
    });
    const refreshed = await getDoc(ref);
    return mapCall(groupId, refreshed.data() as Record<string, unknown>);
  }

  /**
   * Leave the call. Ends it when nobody remains, or when `endCall` is set
   * (last connected person hanging up, including stale presence ids).
   */
  static async leaveCall(
    groupId: string,
    userId: string,
    options?: { endCall?: boolean }
  ): Promise<void> {
    const ref = doc(db, CALLS, groupId);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) return;

      const data = snap.data() as Record<string, unknown>;
      if (data.status !== 'live') return;

      const next = nextTeamUpdateCallAfterLeave({
        participantIds: data.participantIds,
        userId,
        endCall: options?.endCall,
      });
      const now = Timestamp.now();

      if (next.status === 'ended') {
        transaction.update(ref, {
          participantIds: [],
          status: 'ended',
          updatedAt: now,
          endedAt: now,
        });
        return;
      }

      transaction.update(ref, {
        participantIds: next.participantIds,
        updatedAt: now,
      });
    });
  }
}
